import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import '../cmpMemberPages.css';
import TextField from '@material-ui/core/TextField';
import {
  CmpMemberContactAccountActionEnum as Action,
  GetMyCmpMemberContactAccountsQuery,
  useGetMyCmpMemberContactAccountsQuery,
  useManageMyCmpMemberContactAccountMutation,
} from '../../../../graphql/types';
import { PhoneNumberFormatCustom } from '../../../common/utilities/PhoneNumberFormat';
import {
  BILLING_HEADING, BILLING_NOTICE, buildCredentialsText, CMP_CONTACT_CARDS, CmpContactCard, CmpContactCardId, CmpContactFields, cmpPendingGrantPath,
  CONTACT_FIELD_KEYS, CONTACTS_NOT_IN_DIRECTORY, formatContactDate, getCardFields, getPrimaryContactFields, GRANT_LABEL, hasPendingGrant,
  INCOMPLETE_CONTACT_HINT, isCompleteContact, isSameAsPrimary, SAME_AS_PRIMARY_HINT, SAME_AS_PRIMARY_WARNING, SAVE_FIRST_HINT,
} from './cmpContactCards';
import { CMP_CONTACT_SECTION, CmpProfileSectionId } from '../CmpProfile/cmpProfileSections';
import { CmpCountrySelect, CmpStreetAddressInput } from '../../../common/address/CmpAddressFields';
import { googleAddressToMemberFields } from '../../../common/address/googleAddressAutocomplete';
import { getUserFacingErrorText, UserFacingError } from '../../../common/utilities/userFacingError';
import i18n from '../../../../locales/i18n';

type BillingAddressField = 'streetAddress' | 'city' | 'province' | 'postalCode' | 'country';
const BILLING_ADDRESS_FIELDS: BillingAddressField[] = ['streetAddress', 'city', 'province', 'postalCode', 'country'];
// Design ACC2:407 "Check here if same as Company Address" (shown once the company address has a value).
const SAME_AS_COMPANY_LABEL = 'Check here if same as Company Address';

// Wave C ACON-2: Company Contacts cards for the Primary Contact (design
// ACC:L354-641 cards, L1386-1432 Cancel Access and Send Credentials modals).
// Oct 16 2026 design: each card saves on its own (D2-D14). A grant tick waits
// for Save Changes; Send Credentials waits until the card has no unsaved changes.

type Account = GetMyCmpMemberContactAccountsQuery['getMyCmpMemberContactAccounts']['accounts'][number];
type Formik = {
  values: unknown;
  errors: unknown;
  touched: unknown;
  submitCount: number;
  setFieldValue: (field: string, value: unknown) => void;
  handleChange: (event: React.ChangeEvent<unknown>) => void;
};
type Classes = Record<'formSection' | 'sectionHeader' | 'sectionTitle' | 'sectionHelperText' | 'formRow' | 'fieldLabel' | 'companyInput' | 'requiredAsterisk', string>;
type CredentialModal = { card: CmpContactCard; name: string; email: string; account: Account; warn: 'loggedIn' | 'resend' | null; done: boolean; downloaded?: boolean };

const RED = '#ED1C24';
const GOLD = '#E1A731';
const TEXT = '#0A0A0A';
const GREEN = '#10B981';
const operationContext = { preserveErrorForCaller: true };
// Wave 1 (Oct 19 2026): one error rule (server text or plain fallback, never raw browser text).
export const contactCardErrorText = (chamberName: string) => {
  const contact = chamberName.trim() || 'your chamber';
  const memberContact = chamberName.trim() || i18n.t('userFacingErrors.yourChamber');
  return {
    grantFailed: `We could not give this contact access. Try again. If it keeps happening, contact ${contact}.`,
    cancelFailed: `We could not cancel this contact's access. Try again. If it keeps happening, contact ${contact}.`,
    sendFailed: `We could not send the credentials. Try again. If it keeps happening, contact ${contact}.`,
    prepareFailed: `We could not prepare the credentials file. Try again. If it keeps happening, contact ${contact}.`,
    loadFailed: 'We could not load the contact logins.',
    // Wave 2 (Oct 20 2026): the backend warning and lock texts are written for
    // chamber staff ("Ask support ..."). Members get these texts (en and fr).
    usesCompanyLogin: i18n.t('userFacingErrors.contactUsesCompanyLogin', { chamberName: memberContact }),
    loginLocked: i18n.t('userFacingErrors.contactLoginLocked', { chamberName: memberContact }),
    notConfirmed: i18n.t('userFacingErrors.credentialsNotConfirmed', { chamberName: memberContact }),
    sentWithWarning: i18n.t('userFacingErrors.credentialsSentWithWarning'),
    fileWithWarning: i18n.t('userFacingErrors.credentialsFileWithWarning'),
  };
};

/** Backend note for a login shared with another chamber (memberDashboardLinkedLoginMessage). It is member-ready. */
const LINKED_LOGIN_NOTE_START = 'This contact signs in with the Member Dashboard login they already use for another chamber';

/** The lock text a member sees on a contact card, or null when the card is not locked. */
export const getContactLockText = (
  account: { usesLegacyMemberAccount?: boolean | null; credentialActionsBlockedReason?: string | null } | undefined,
  text: Pick<ReturnType<typeof contactCardErrorText>, 'usesCompanyLogin' | 'loginLocked'>,
): string | null => {
  const reason = account?.credentialActionsBlockedReason?.trim() || '';
  if (reason.startsWith(LINKED_LOGIN_NOTE_START)) return reason;
  if (account?.usesLegacyMemberAccount) return text.usesCompanyLogin;
  return reason ? text.loginLocked : null;
};

const modalButton = (background: string): React.CSSProperties => ({
  flex: 1, background, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '13px 0', fontFamily: "'DM Sans', sans-serif",
  fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%',
});
const busyStyle = (busy: boolean): React.CSSProperties => (busy ? { opacity: 0.7, cursor: 'default' } : {});
const ModalIcon = ({ background, children }: { background: string; children: React.ReactNode }) => (
  <div style={{ width: 64, height: 64, borderRadius: '50%', background, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF' }}>{children}</div>
);
const SendIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
);

const SmallModal = ({ onClose, label, children }: { onClose: () => void; label: string; children: React.ReactNode }) => (
  <Dialog open onClose={onClose} aria-label={label} maxWidth={false}
    PaperProps={{ style: { width: 'min(420px, 92vw)', borderRadius: 16, margin: 0, position: 'relative', padding: '32px 32px 28px', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12, fontFamily: "'DM Sans', sans-serif", color: TEXT } }}>
    <button type="button" aria-label="Close" onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: '50%',
      background: RED, color: '#FFFFFF', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>✕</button>
    {children}
  </Dialog>
);

export type CmpContactCardsBridge = {
  /** True when the contact has Member Dashboard access saved on the backend. */
  isEnabled: (contactId: CmpContactCardId) => boolean;
  /**
   * Runs after a profile save: reloads the access rows (fresh member token),
   * then grants access to the saved cards that have a grant tick waiting.
   */
  afterSave: (sections: CmpProfileSectionId[]) => Promise<void>;
  /** Clears the card notes after Reset. */
  onReset: (section: CmpProfileSectionId) => void;
};

export default function CmpCompanyContactCards({ formik, classes, chamberName, timeZone, savedRevision, dirtySections = {}, attemptedSections = {},
  renderFooter, bridge, markedField }: {
  formik: Formik; classes: Classes; chamberName: string; timeZone?: string | null;
  /** Changes after each profile save, so the access rows reload. */
  savedRevision?: number | string | null;
  /** Cards with unsaved changes (D9, D10, D11). */
  dirtySections?: Partial<Record<CmpProfileSectionId, boolean>>;
  /** Cards with a Save Changes or Update attempt (D13). */
  attemptedSections?: Partial<Record<CmpProfileSectionId, boolean>>;
  /** D2 card footer (Reset, Save Changes, Last Saved). */
  renderFooter?: (section: CmpProfileSectionId) => React.ReactNode;
  bridge?: React.MutableRefObject<CmpContactCardsBridge | undefined>;
  /** MD-N6: true while a field is marked red after the error pop-up. */
  markedField?: (path: string) => boolean;
}) {
  const errorText = contactCardErrorText(chamberName);
  const accountsQuery = useGetMyCmpMemberContactAccountsQuery({ fetchPolicy: 'network-only', context: operationContext });
  const { refetch } = accountsQuery;
  useEffect(() => { if (savedRevision) void refetch(); }, [savedRevision, refetch]);
  const [manage] = useManageMyCmpMemberContactAccountMutation({ context: operationContext });
  const [busyContact, setBusyContact] = useState<string>();
  const [hint, setHint] = useState<Record<string, string | undefined>>({});
  const [sameWarning, setSameWarning] = useState<Record<string, boolean>>({});
  const [sameHint, setSameHint] = useState<Record<string, boolean>>({});
  const [sendAttempted, setSendAttempted] = useState<Record<string, boolean>>({});
  const [revoke, setRevoke] = useState<{ card: CmpContactCard; name: string; done: boolean }>();
  const [credentials, setCredentials] = useState<CredentialModal>();
  const [modalError, setModalError] = useState<string>();
  // Double-click guard (item 13301855299 Part B): one modal action at a time.
  const inFlight = useRef(false);
  const [modalBusy, setModalBusy] = useState<'send' | 'download' | 'revoke'>();
  const beforeSame = useRef<Record<string, CmpContactFields>>({});
  // M2 (design (4) CMA:2194-2222 + Q15): 'auto' reads the saved address; 'same' = member ticked the box;
  // 'different' = member unticked it or edited a billing field.
  const [billingChoice, setBillingChoice] = useState<'auto' | 'same' | 'different'>('auto');
  const values = formik.values as { address?: Partial<Record<BillingAddressField, string>>; cmpBillingAddress?: Partial<Record<BillingAddressField, string>> };
  const companyAddress = values.address || {};
  const storedBilling = values.cmpBillingAddress || {};
  const companyHasAddress = (['streetAddress', 'city', 'postalCode', 'country'] as BillingAddressField[]).some(field => (companyAddress[field] || '').trim());
  const companyKey = BILLING_ADDRESS_FIELDS.map(field => companyAddress[field] || '').join('|');
  const norm = (value?: string) => (value || '').trim().toLowerCase();
  const billingMatchesCompany = BILLING_ADDRESS_FIELDS.every(field => norm(storedBilling[field]) === norm(companyAddress[field]));
  // Q15: a billing address with no street, city, province or postal code means "same as company" (invoices use the
  // company address then). The saved blank stays blank until the member changes a billing field.
  const billingEmpty = !(['streetAddress', 'city', 'province', 'postalCode'] as BillingAddressField[]).some(field => (storedBilling[field] || '').trim());
  const billingSame = companyHasAddress
    && (billingChoice === 'same' || billingMatchesCompany || (billingChoice === 'auto' && billingEmpty));
  // While the box is ticked the billing address follows the company address.
  useEffect(() => {
    if (billingChoice !== 'same') return;
    BILLING_ADDRESS_FIELDS.forEach(field => formik.setFieldValue(`cmpBillingAddress.${field}`, companyAddress[field] || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingChoice, companyKey]);
  useEffect(() => { if (!companyHasAddress && billingChoice === 'same') setBillingChoice('auto'); }, [companyHasAddress, billingChoice]);
  // A save or a reload reads the saved address again.
  useEffect(() => { setBillingChoice('auto'); }, [savedRevision]);
  const setBillingSameChecked = (checked: boolean) => {
    if (checked) { setBillingChoice('same'); return; }
    setBillingChoice('different');
    BILLING_ADDRESS_FIELDS.forEach(field => formik.setFieldValue(`cmpBillingAddress.${field}`, ''));
  };
  const setBillingField = (field: BillingAddressField, value: string) => {
    // Editing from the "same" state starts from the company address (design billingVals set()).
    if (billingSame) BILLING_ADDRESS_FIELDS.forEach(other => formik.setFieldValue(`cmpBillingAddress.${other}`, companyAddress[other] || ''));
    setBillingChoice('different');
    formik.setFieldValue(`cmpBillingAddress.${field}`, value);
  };
  const billingValue = (field: BillingAddressField) => (billingSame ? companyAddress[field] : storedBilling[field]) || '';

  const data = accountsQuery.data?.getMyCmpMemberContactAccounts;
  const accounts = data?.accounts || [];
  // The latest member token: from the query, a refetch or a mutation result.
  const token = useRef<number | undefined>(data?.memberUpdatedAtUtcMilli);
  const queryToken = data?.memberUpdatedAtUtcMilli;
  useEffect(() => { if (queryToken !== undefined) token.current = queryToken; }, [queryToken]);
  const primary = getPrimaryContactFields(formik.values);
  const setCardHint = (contactId: string, value?: string) => setHint(current => ({ ...current, [contactId]: value }));

  const reload = async () => {
    const fresh = await refetch();
    const freshToken = fresh?.data?.getMyCmpMemberContactAccounts?.memberUpdatedAtUtcMilli;
    if (typeof freshToken === 'number') token.current = freshToken;
  };

  const runAction = async (card: CmpContactCard, action: Action) => {
    // The token is enough: after a card save the cards can load again, and reload() already set a fresh token.
    if (token.current === undefined) throw new Error('Contact access is still loading. Try again in a moment.');
    const result = await manage({ variables: { input: { contactId: card.contactId, action,
      expectedMemberUpdatedAtUtcMilli: token.current } } });
    const resultToken = result.data?.manageMyCmpMemberContactAccount?.memberUpdatedAtUtcMilli;
    if (typeof resultToken === 'number') token.current = resultToken;
    await reload();
    return result.data?.manageMyCmpMemberContactAccount;
  };

  const guarded = async (kind: 'send' | 'download' | 'revoke', action: () => Promise<void>) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setModalBusy(kind);
    try { await action(); } finally { inFlight.current = false; setModalBusy(undefined); }
  };

  const setCardFields = (card: CmpContactCard, fields: CmpContactFields) =>
    CONTACT_FIELD_KEYS.forEach(key => formik.setFieldValue(card.fieldNames[key], fields[key]));

  // MD-M2 (ACC:2047-2052): while "same as primary" is ticked the card follows later
  // Primary Contact edits. A contact on the old company login is copied once only.
  const linkedToPrimary = useRef<Record<string, boolean>>({});
  const lastPrimary = useRef(primary);
  const primaryKey = CONTACT_FIELD_KEYS.map(key => primary[key]).join('\u0001');
  useEffect(() => {
    const before = lastPrimary.current;
    lastPrimary.current = primary;
    if (CONTACT_FIELD_KEYS.every(key => before[key] === primary[key])) return;
    CMP_CONTACT_CARDS.forEach(card => {
      if (accounts.find(row => row.contactId === card.contactId)?.usesLegacyMemberAccount) return;
      const fields = getCardFields(formik.values, card);
      const following = linkedToPrimary.current[card.contactId] && CONTACT_FIELD_KEYS.every(key => fields[key] === before[key]);
      if (!following && !isSameAsPrimary(fields, before)) return;
      linkedToPrimary.current[card.contactId] = true;
      setCardFields(card, primary);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryKey]);

  const toggleSame = (card: CmpContactCard, fields: CmpContactFields, same: boolean) => {
    if (same) {
      linkedToPrimary.current[card.contactId] = false;
      setSameWarning(current => ({ ...current, [card.contactId]: false }));
      setCardFields(card, beforeSame.current[card.contactId] || { name: '', title: '', email: '', phone: '', ext: '', mobile: '' });
      return;
    }
    if (!primary.name.trim() || !primary.email.trim()) { setSameWarning(current => ({ ...current, [card.contactId]: true })); return; }
    setSameWarning(current => ({ ...current, [card.contactId]: false }));
    beforeSame.current[card.contactId] = fields;
    linkedToPrimary.current[card.contactId] = true;
    setCardFields(card, primary);
  };

  // D12: the tick is local until Save Changes; unticking saved access asks to cancel it.
  const grant = (card: CmpContactCard, fields: CmpContactFields, account: Account | undefined, same: boolean) => {
    const pendingPath = cmpPendingGrantPath(card.contactId);
    setCardHint(card.contactId);
    if (account?.enabled === true) { setRevoke({ card, name: fields.name || 'This contact', done: false }); return; }
    if (hasPendingGrant(formik.values, card.contactId)) { formik.setFieldValue(pendingPath, false); return; }
    if (same) { setSameHint(current => ({ ...current, [card.contactId]: true })); return; }
    setSameHint(current => ({ ...current, [card.contactId]: false }));
    formik.setFieldValue(pendingPath, true);
  };

  if (bridge) {
    bridge.current = {
      isEnabled: contactId => accounts.some(row => row.contactId === contactId && row.enabled === true),
      afterSave: async sections => {
        const waiting = CMP_CONTACT_CARDS.filter(card => sections.includes(CMP_CONTACT_SECTION[card.contactId]) && hasPendingGrant(formik.values, card.contactId));
        try { await reload(); } catch (error) {
          waiting.forEach(card => setCardHint(card.contactId, getUserFacingErrorText(error, errorText.grantFailed)));
          return;
        }
        for (const card of waiting) {
          setBusyContact(card.contactId); setCardHint(card.contactId);
          try {
            await runAction(card, Action.Enable);
            formik.setFieldValue(cmpPendingGrantPath(card.contactId), false);
          } catch (error) {
            setCardHint(card.contactId, getUserFacingErrorText(error, errorText.grantFailed));
          } finally { setBusyContact(undefined); }
        }
      },
      onReset: section => {
        const card = CMP_CONTACT_CARDS.find(item => CMP_CONTACT_SECTION[item.contactId] === section);
        if (!card) return;
        setCardHint(card.contactId);
        setSameHint(current => ({ ...current, [card.contactId]: false }));
        setSameWarning(current => ({ ...current, [card.contactId]: false }));
        setSendAttempted(current => ({ ...current, [card.contactId]: false }));
        if (card.billingAddress) setBillingChoice('auto');
      },
    };
  }

  const confirmRevoke = () => guarded('revoke', async () => {
    if (!revoke) return;
    setModalError(undefined);
    try { await runAction(revoke.card, Action.Disable); setRevoke({ ...revoke, done: true }); setModalError(undefined); }
    catch (error) { setModalError(getUserFacingErrorText(error, errorText.cancelFailed)); }
  });

  const sendCredentials = () => guarded('send', async () => {
    if (!credentials) return;
    setModalError(undefined);
    try {
      const action = credentials.account.credentialsSentAtUtcMilli ? Action.ResetPassword : Action.SendCredentials;
      const result = await runAction(credentials.card, action);
      if (!result?.deliveryConfirmed) throw new UserFacingError(errorText.notConfirmed);
      setCredentials({ ...credentials, done: true });
      setModalError(result.trackingWarning ? errorText.sentWithWarning : undefined);
    } catch (error) {
      setModalError(getUserFacingErrorText(error, errorText.sendFailed));
    }
  });

  const downloadCredentials = () => guarded('download', async () => {
    if (!credentials) return;
    setModalError(undefined);
    try {
      const result = await runAction(credentials.card, Action.DownloadCredentials);
      const prepared = result?.credentials;
      if (!prepared) throw new Error('No credentials came back.');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([buildCredentialsText({ chamberName, name: credentials.name, email: prepared.email,
        password: prepared.password, loginUrl: prepared.loginUrl || window.location.origin })], { type: 'text/plain' }));
      link.download = `Member Dashboard Credentials - ${credentials.name || 'Contact'}.txt`;
      document.body.appendChild(link); link.click(); link.remove();
      // Design (ACC:2198-2205): the file downloads and the window stays on Send Credentials (MD-U13).
      setModalError(result?.trackingWarning ? errorText.fileWithWarning : undefined);
    } catch (error) {
      setModalError(getUserFacingErrorText(error, errorText.prepareFailed));
    }
  });

  const pathValue = (source: unknown, path: string): unknown => path.split('.')
    .reduce<unknown>((value, key) => (value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined), source);
  const input = (card: CmpContactCard, key: keyof CmpContactFields, placeholder: string, phone = false) => {
    const path = card.fieldNames[key];
    const error = pathValue(formik.errors, path);
    const shown = typeof error === 'string' && (!!pathValue(formik.touched, path) || formik.submitCount > 0) ? error : undefined;
    return (
      <div data-field={path} className={markedField?.(path) ? 'cmp-field-error' : undefined} style={{ minWidth: 0 }}>
      <TextField placeholder={placeholder} className={classes.companyInput} variant="outlined" name={path}
        inputProps={{ autoComplete: 'new-password', 'aria-label': `${card.title} ${key}` }}
        value={getCardFields(formik.values, card)[key]}
        onChange={event => { linkedToPrimary.current[card.contactId] = false; formik.handleChange(event); }}
        error={!!shown} helperText={shown}
        InputProps={phone ? { inputComponent: PhoneNumberFormatCustom as never } : undefined} />
      </div>
    );
  };
  const billingInput = (field: string, label: string, placeholder: string) => (
    <div>
      <div className={classes.fieldLabel}>{label}</div>
      <TextField placeholder={placeholder} className={classes.companyInput} variant="outlined" name={`cmpBillingAddress.${field}`}
        inputProps={{ autoComplete: 'new-password', 'aria-label': `Billing ${label}` }}
        value={billingValue(field as BillingAddressField)}
        onChange={event => setBillingField(field as BillingAddressField, event.target.value)} />
    </div>
  );

  return (
    <>
      {accountsQuery.error && (
        <p role="alert" style={{ maxWidth: 1096, margin: '0 auto', color: RED, fontWeight: 600 }}>
          {getUserFacingErrorText(accountsQuery.error, errorText.loadFailed)}{' '}
          <button type="button" onClick={() => accountsQuery.refetch()} style={{ textDecoration: 'underline', background: 'none', border: 'none', color: RED, cursor: 'pointer', fontWeight: 600 }}>Retry</button>
        </p>
      )}
      {CMP_CONTACT_CARDS.map(card => {
        const section = CMP_CONTACT_SECTION[card.contactId];
        const fields = getCardFields(formik.values, card);
        const account = accounts.find(row => row.contactId === card.contactId);
        // A linked card stays ticked while the Primary Contact is part-way through an edit.
        const same = isSameAsPrimary(fields, primary)
          || (!!linkedToPrimary.current[card.contactId] && CONTACT_FIELD_KEYS.every(key => fields[key] === primary[key]));
        const enabled = account?.enabled === true;
        const pending = hasPendingGrant(formik.values, card.contactId);
        const granted = enabled || pending;
        const dirty = !!dirtySections[section];
        const blocked = getContactLockText(account, errorText);
        const complete = isCompleteContact(fields);
        const busy = busyContact === card.contactId;
        const sentAt = enabled ? formatContactDate(account?.credentialsSentAtUtcMilli, timeZone) : null;
        const lastLogin = enabled ? formatContactDate(account?.lastDashboardLoginAtUtcMilli, timeZone) : null;
        // D10: Send Credentials waits for Save Changes.
        const canSend = enabled && !blocked && !busy && !dirty;
        // As on the staff page, a note stops new access but never stops removing
        // access (for example a login connected from another chamber, M5).
        const canRevoke = enabled && !account?.usesLegacyMemberAccount;
        // D11: Cancel Access only for saved, complete access.
        const showCancel = canRevoke && !dirty && complete;
        const grantLocked = busy || (!!blocked && !canRevoke);
        // D13/D14 (design hintStyle): the same-as-primary note after a tick, or the
        // name and email note after a save or send attempt.
        const designHint = same && sameHint[card.contactId] && complete ? SAME_AS_PRIMARY_HINT
          : !complete && granted && (attemptedSections[section] || sendAttempted[card.contactId]) ? INCOMPLETE_CONTACT_HINT : null;
        const note = hint[card.contactId] || designHint || blocked;
        const star = card.requiredStars ? <span className={classes.requiredAsterisk}>*</span> : null;
        return (
          <div key={card.contactId} className={classes.formSection} data-testid={`cmp-contact-${card.contactId}`} data-section={section}>
            <div className={classes.sectionHeader}><p className={classes.sectionTitle}>{card.title}</p></div>
            <p className={classes.sectionHelperText} style={{ margin: '0 0 8px' }}>{card.intro}</p>
            <p className={classes.sectionHelperText} style={{ fontWeight: 600, margin: '0 0 24px' }}>{CONTACTS_NOT_IN_DIRECTORY}</p>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '0 0 16px', cursor: 'pointer' }}>
              <input type="checkbox" className="cmp-check" checked={same} onChange={() => toggleSame(card, fields, same)}
                style={{ width: 18, height: 18, margin: '1px 0 0' }} />
              <span style={{ fontSize: 14, color: TEXT, lineHeight: 1.55 }}>{card.sameAsPrimaryLabel}</span>
            </label>
            {sameWarning[card.contactId] && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED, margin: '-8px 0 16px', paddingLeft: 28 }}>{SAME_AS_PRIMARY_WARNING}</div>}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '0 0 24px', cursor: grantLocked ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={granted} disabled={grantLocked} onChange={() => grant(card, fields, account, same)}
                aria-label={`Grant ${card.title} access`}
                className="cmp-check" style={{ width: 18, height: 18, margin: '1px 0 0', opacity: grantLocked ? 0.5 : 1 }} />
              <span style={{ fontSize: 14, lineHeight: 1.55, color: grantLocked ? '#9A9A9A' : TEXT }}>{GRANT_LABEL}</span>
            </label>
            {note && (
              <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED, margin: '-16px 0 24px', paddingLeft: 28, lineHeight: 1.5 }}>{note}</div>
            )}
            {/* MD-M8 (ACC:366, 379): 1 : 1.6 columns, phone row number / 60px Ext. / mobile. */}
            <div className="cmp-contact-grid">
              <div style={{ minWidth: 0 }}>
                <div className={classes.fieldLabel}><span>Contact Name{star}</span></div>
                {input(card, 'name', 'Enter Contact Name')}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className={classes.fieldLabel}>Title</div>
                {input(card, 'title', 'Enter Contact Person Title')}
              </div>
              <div style={{ minWidth: 0 }}>
                <div className={classes.fieldLabel}><span>Contact Email{star}</span></div>
                {input(card, 'email', 'Enter Contact Email')}
              </div>
              <div className="cmp-contact-phone-row">
                <div style={{ minWidth: 0 }}><div className={classes.fieldLabel} style={{ whiteSpace: 'nowrap' }}>Company Number</div>{input(card, 'phone', 'Company Number', true)}</div>
                <div style={{ minWidth: 0 }}><div className={classes.fieldLabel} style={{ whiteSpace: 'nowrap' }}>Ext.</div>{input(card, 'ext', '0000')}</div>
                <div style={{ minWidth: 0 }}><div className={classes.fieldLabel} style={{ whiteSpace: 'nowrap' }}>Mobile Number</div>{input(card, 'mobile', 'Mobile Number', true)}</div>
              </div>
            </div>
            <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #9E9E9E' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 28, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <button type="button" disabled={!canSend} data-testid={`send-credentials-${card.contactId}`}
                    onClick={() => {
                      if (!account) return;
                      // D14: granted without a name and email shows the note instead of the window.
                      if (!complete) { setSendAttempted(current => ({ ...current, [card.contactId]: true })); return; }
                      setModalError(undefined);
                      setCredentials({ card, name: fields.name, email: fields.email, account, done: false,
                        warn: account.credentialsSentAtUtcMilli && account.lastDashboardLoginAtUtcMilli ? 'loggedIn' : account.credentialsSentAtUtcMilli ? 'resend' : null });
                    }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '11px 22px',
                      fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: canSend ? 'pointer' : 'not-allowed',
                      background: !canSend ? '#CFCFCF' : lastLogin ? '#9E9E9E' : GOLD }}>
                    <SendIcon />Send Credentials
                  </button>
                  <span style={{ fontSize: 14, color: TEXT }}>Last Sent: {sentAt || 'Never'}</span>
                  {dirty && granted && (
                    <span data-testid={`save-first-${card.contactId}`} style={{ display: 'block', fontSize: 12, fontWeight: 600, color: RED, maxWidth: 200, textAlign: 'center', lineHeight: 1.4 }}>{SAVE_FIRST_HINT}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 41 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: lastLogin ? GREEN : '#9A9A9A' }} />
                  <span style={{ fontSize: 16, fontWeight: 600, color: lastLogin ? TEXT : '#9A9A9A' }}>{lastLogin ? `Last Login: ${lastLogin}` : 'Never Logged In'}</span>
                </div>
                {showCancel && (
                  <button type="button" onClick={() => { setModalError(undefined); setRevoke({ card, name: fields.name || 'This contact', done: false }); }}
                    style={{ marginLeft: 'auto', background: RED, color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '11px 22px',
                      fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel Access</button>
                )}
              </div>
            </div>
            {card.billingAddress && (
              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #9E9E9E' }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: TEXT, margin: '0 0 8px' }}>{BILLING_HEADING}</div>
                {/* Design bill_noticeStyle: red notice only while the billing address is the company address. */}
                {billingSame && <p className={classes.sectionHelperText} style={{ color: RED, fontWeight: 600 }}>{BILLING_NOTICE}</p>}
                {/* Design spacing (ACC:L475-499): 24px between the address rows. */}
                {companyHasAddress && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 20px', cursor: 'pointer', fontSize: 14, color: TEXT }}>
                    <input type="checkbox" className="cmp-check" checked={billingSame} onChange={event => setBillingSameChecked(event.target.checked)}
                      style={{ width: 18, height: 18, margin: 0 }} />
                    <span>{SAME_AS_COMPANY_LABEL}</span>
                  </label>
                )}
                <div style={{ marginBottom: 24 }}>
                  <div className={classes.fieldLabel}>Street Address</div>
                  <CmpStreetAddressInput name="cmpBillingAddress.streetAddress" ariaLabel="Billing Street Address"
                    placeholder="Enter Street Address, Example: 123 Maple E.g." value={billingValue('streetAddress')}
                    onChange={value => setBillingField('streetAddress', value)}
                    onAddressSelect={address => {
                      const fields = googleAddressToMemberFields(address);
                      setBillingChoice('different');
                      formik.setFieldValue('cmpBillingAddress.streetAddress', fields.streetAddress);
                      formik.setFieldValue('cmpBillingAddress.city', fields.city);
                      formik.setFieldValue('cmpBillingAddress.province', fields.provinceOrState);
                      formik.setFieldValue('cmpBillingAddress.postalCode', fields.postalOrZipCode);
                      formik.setFieldValue('cmpBillingAddress.country', fields.country);
                    }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '24px 20px' }}>
                  {billingInput('city', 'City', 'Enter City')}
                  {billingInput('province', 'Province/State', 'Enter Province/State')}
                  {billingInput('postalCode', 'Postal/Zip Code', 'Enter Postal/Zip Code')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '24px 20px', marginTop: 24 }}>
                  <div>
                    <div className={classes.fieldLabel}>Country</div>
                    <CmpCountrySelect ariaLabel="Billing Country" value={billingValue('country')} onChange={country => setBillingField('country', country)} />
                  </div>
                  <div /><div />
                </div>
              </div>
            )}
            {renderFooter?.(section)}
          </div>
        );
      })}
      {revoke && (
        <SmallModal label="Cancel Access" onClose={() => { setRevoke(undefined); setModalError(undefined); }}>
          <ModalIcon background={RED}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg></ModalIcon>
          {revoke.done ? (<>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Access Denied</div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}><b>{revoke.name}</b> no longer has access to your member dashboard. You can set up a new contact and grant them access.</div>
          </>) : (<>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Cancel Access?</div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}>Are you sure you want to cancel member dashboard access for <b>{revoke.name}</b>? Their login credentials will no longer work.</div>
            <button type="button" onClick={confirmRevoke} disabled={!!modalBusy} style={{ ...modalButton(RED), marginTop: 12, ...busyStyle(!!modalBusy) }}>
              {modalBusy === 'revoke' ? 'Preparing...' : 'Yes'}
            </button>
          </>)}
          {modalError && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED }}>{modalError}</div>}
        </SmallModal>
      )}
      {credentials && (
        <SmallModal label="Send Credentials" onClose={() => { setCredentials(undefined); setModalError(undefined); }}>
          {credentials.done ? (<>
            <ModalIcon background={GREEN}><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12.5l5 5L20 7" /></svg></ModalIcon>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Credentials Sent</div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}>Login credentials have been sent to <b>{credentials.email}</b>.</div>
          </>) : credentials.warn ? (<>
            <ModalIcon background={GOLD}><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3L2 20h20L12 3z" /><path d="M12 10v4M12 17h.01" /></svg></ModalIcon>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{credentials.warn === 'loggedIn' ? 'Contact Will Be Logged Out' : 'Reset Contact Credentials?'}</div>
            <div style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-line' }}>
              {credentials.warn === 'loggedIn'
                ? `${credentials.name} last logged in on ${formatContactDate(credentials.account.lastDashboardLoginAtUtcMilli, timeZone)}.\n\nSending new credentials will reset their password and log them out of the Member Dashboard. They must sign in again using the new credentials.`
                : `Credentials have been sent to ${credentials.name}, but they have not logged in yet.\n\nSending new credentials will reset their password and invalidate the previously sent credentials.`}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Do you want to continue?</div>
            <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 12 }}>
              <button type="button" onClick={() => setCredentials({ ...credentials, warn: null })} style={modalButton(GOLD)}>Yes, Continue</button>
              <button type="button" onClick={() => setCredentials(undefined)} style={modalButton(TEXT)}>No</button>
            </div>
          </>) : (<>
            <ModalIcon background={GOLD}><SendIcon size={28} /></ModalIcon>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Send Credentials</div>
            <div style={{ fontSize: 14, lineHeight: 1.55 }}>Member Dashboard login credentials will be emailed to <b>{credentials.name}</b> at <b>{credentials.email}</b>.</div>
            <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 12 }}>
              <button type="button" onClick={sendCredentials} disabled={!!modalBusy} style={{ ...modalButton(GOLD), ...busyStyle(!!modalBusy) }}>
                {modalBusy === 'send' ? 'Preparing...' : 'Send Credentials'}
              </button>
            </div>
            <button type="button" onClick={downloadCredentials} disabled={!!modalBusy} style={{ ...modalButton(TEXT), ...busyStyle(!!modalBusy) }}>
              {modalBusy === 'download' ? 'Preparing...' : 'Download Credentials'}
            </button>
          </>)}
          {modalError && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED }}>{modalError}</div>}
        </SmallModal>
      )}
    </>
  );
}
